"use client"

import { useEffect, useMemo, useRef } from "react"
import type { Editor } from "@tiptap/react"
import { BookOpen, FileText, Plus, StickyNote } from "lucide-react"

import { getLinkCandidates } from "@/api/services/links"
import type { DocLinkAttrs } from "@/components/tiptap-node/doc-link-node/doc-link-node-extension"
import type {
  SuggestionItem,
  SuggestionMenuProps,
  SuggestionMenuRenderProps,
} from "@/components/tiptap-ui-utils/suggestion-menu"
import { SuggestionMenu } from "@/components/tiptap-ui-utils/suggestion-menu"
import { Button } from "@/components/tiptap-ui-primitive/button"
import {
  Card,
  CardBody,
  CardGroupLabel,
  CardItemGroup,
} from "@/components/tiptap-ui-primitive/card"
import { getElementOverflowPosition } from "@/lib/tiptap-collab-utils"
import { useMemoStore } from "@/store/useMemoStore"

type DocLinkDropdownMenuProps = Omit<
  SuggestionMenuProps,
  "items" | "children"
>

const SELECTOR = "tiptap-doc-link-dropdown-menu"

// 半角の [[ と、日本語入力中に打ちやすい「「 の両方で開く
const TRIGGERS = [
  { char: "[[", pluginKey: "docLinkDropdownMenu" },
  { char: "「「", pluginKey: "docLinkDropdownMenuJa" },
]

export const DocLinkDropdownMenu = (props: DocLinkDropdownMenuProps) => (
  <>
    {TRIGGERS.map((trigger) => (
      <SuggestionMenu
        key={trigger.pluginKey}
        char={trigger.char}
        pluginKey={trigger.pluginKey}
        allowSpaces
        allowedPrefixes={null}
        decorationClass="tiptap-doc-link-decoration"
        selector={SELECTOR}
        items={getSuggestionItems}
        {...props}
      >
        {(renderProps) => <List {...renderProps} />}
      </SuggestionMenu>
    ))}
  </>
)

const insertLink = (editor: Editor, attrs: DocLinkAttrs) => {
  editor.chain().focus().insertDocLink(attrs).run()
}

const getSuggestionItems = async ({
  query,
}: {
  query: string
  editor: Editor
}): Promise<SuggestionItem<DocLinkAttrs>[]> => {
  const q = query.replace(/(\]\]|」」)$/, "").trim()
  let candidates: Awaited<ReturnType<typeof getLinkCandidates>> = []
  try {
    candidates = await getLinkCandidates(q)
  } catch (err) {
    console.warn("[doc-link] failed to load candidates", err)
  }

  const items: SuggestionItem<DocLinkAttrs>[] = candidates.map((c) => {
    const attrs: DocLinkAttrs = {
      targetType: c.target_type,
      targetId: c.target_id,
      label: c.title || "無題のメモ",
    }
    const isBook = c.target_type === "product"
    return {
      title: attrs.label,
      subtext: isBook && c.has_note ? "読書ノートあり" : undefined,
      badge: isBook ? BookOpen : c.is_card ? StickyNote : FileText,
      group: isBook ? "本" : c.is_card ? "カード" : "メモ",
      context: attrs,
      onSelect: ({ editor, context }) => {
        if (context) insertLink(editor, context)
      },
    }
  })

  const hasSameMemo = candidates.some((c) => c.target_type === "memo" && c.title === q)
  if (q && !hasSameMemo) {
    const create = (isCard: boolean): SuggestionItem<DocLinkAttrs> => ({
      title: `「${q}」という${isCard ? "カード" : "メモ"}を作る`,
      badge: isCard ? StickyNote : Plus,
      group: "新規",
      onSelect: ({ editor }) => {
        void useMemoStore
          .getState()
          .addMemo(q, isCard)
          .then((memo) => {
            if (memo) insertLink(editor, { targetType: "memo", targetId: memo.id, label: q })
          })
      },
    })
    items.push(create(true), create(false))
  }
  return items
}

const Item = ({
  item,
  isSelected,
  onSelect,
}: {
  item: SuggestionItem<DocLinkAttrs>
  isSelected: boolean
  onSelect: () => void
}) => {
  const itemRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const container = document.querySelector(`[data-selector="${SELECTOR}"]`) as HTMLElement | null
    if (!itemRef.current || !isSelected || !container) return
    const overflow = getElementOverflowPosition(itemRef.current, container)
    if (overflow === "top") itemRef.current.scrollIntoView(true)
    else if (overflow === "bottom") itemRef.current.scrollIntoView(false)
  }, [isSelected])

  const BadgeIcon = typeof item.badge === "string" ? null : item.badge

  return (
    <Button
      ref={itemRef}
      variant="ghost"
      data-active-state={isSelected ? "on" : "off"}
      onClick={onSelect}
    >
      {BadgeIcon && <BadgeIcon className="tiptap-button-icon" />}
      <div className="tiptap-button-text">
        {item.title}
        {item.subtext && (
          <span style={{ marginLeft: 6, fontSize: "0.75em", opacity: 0.6 }}>{item.subtext}</span>
        )}
      </div>
    </Button>
  )
}

const List = ({ items, selectedIndex, onSelect }: SuggestionMenuRenderProps<DocLinkAttrs>) => {
  const groups = useMemo(() => {
    const byLabel = new Map<string, { item: SuggestionItem<DocLinkAttrs>; index: number }[]>()
    items.forEach((item, index) => {
      const label = item.group ?? ""
      byLabel.set(label, [...(byLabel.get(label) ?? []), { item, index }])
    })
    return [...byLabel.entries()]
  }, [items])

  if (!items.length) return null

  return (
    <Card
      className="tiptap-slash-card"
      style={{ maxHeight: "var(--suggestion-menu-max-height)" }}
    >
      <CardBody className="tiptap-slash-card-body">
        {groups.map(([label, entries]) => (
          <CardItemGroup key={label}>
            {label && <CardGroupLabel>{label}</CardGroupLabel>}
            {entries.map(({ item, index }) => (
              <Item
                key={`${index}-${item.title}`}
                item={item}
                isSelected={index === selectedIndex}
                onSelect={() => onSelect(item)}
              />
            ))}
          </CardItemGroup>
        ))}
      </CardBody>
    </Card>
  )
}
